import { useEffect, useRef, type ReactNode } from 'react';
import { Icon } from './Icon.js';
import { IconButton } from './IconButton.js';
import './Modal.css';

export interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Uppercase kicker over the title — "Processing mode", "Confirm merge". */
  eyebrow?: string;
  title: string;
  /** The explanatory sentence under the title. */
  subtitle?: ReactNode;
  children: ReactNode;
  /** Left-aligned footer hint; actions go in `actions`. */
  hint?: ReactNode;
  /** Right-aligned footer actions — secondary first, primary last, per the prototype. */
  actions?: ReactNode;
  /**
   * Width. The prototype uses five, and they are not interchangeable — the width is
   * how a modal signals how much it is about to ask of you:
   *
   *   `sm`  560  a confirmation with one question (withdraw approval)
   *   `md`  620  a consent or scope-escalation flow (the default)
   *   `lg`  680  an explainer with side-by-side options (the mode picker)
   *   `xl`  720  a multi-stage approval with a stepper in its header
   *
   * `narrow` is kept as a deprecated alias for `sm`, since both apps already pass it.
   */
  width?: 'sm' | 'md' | 'lg' | 'xl';
  /** @deprecated Use `width="sm"`. */
  narrow?: boolean;
  /**
   * Rendered inside the header band, under the title — a `Stepper` for a multi-stage
   * flow. A slot rather than a `steps` prop, because the modal should not know what
   * kind of progress it is showing.
   */
  progress?: ReactNode;
  /**
   * Set on a modal opened **from another modal**. The prototype stacks them — scope
   * escalation over a source detail, un-merge over action history — using six distinct
   * z-indices, which is unmaintainable. This raises the layer by one step instead.
   *
   * The scrim is also suppressed when stacked: two scrims compound to near-opaque and
   * make the parent look broken rather than backgrounded.
   */
  stacked?: boolean;
  closeLabel?: string;
}

/**
 * The centred modal (design-system catalogue, Tier 2) — previously Planned as
 * "likely a thin `Panel` variant", which the fidelity audit showed it is not
 * (S-i): its radius, scrim, elevation, max-height and three-band chrome are all
 * its own, and every one is now a token this component consumes.
 *
 * Unlike `Menu` (deliberately not focus-trapped), a modal IS a dialog: focus
 * moves in on open, Tab cycles inside, Escape closes, and focus returns to the
 * opener on close (a11y.md §4). The page behind is `inert` semantics via
 * `aria-modal`, and the scrim click closes — matching the prototype, where every
 * modal is dismissible (destructive confirmations gate on their typed input, not
 * on trapping the user).
 */
export function Modal({
  open,
  onOpenChange,
  eyebrow,
  title,
  subtitle,
  progress,
  width,
  stacked = false,
  children,
  hint,
  actions,
  narrow = false,
  closeLabel = 'Close',
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    openerRef.current = document.activeElement as HTMLElement | null;
    // Move focus to the panel itself, not the close button: focusing close first
    // announces "Close" before the title, which is backwards.
    panelRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onOpenChange(false);
        return;
      }
      if (event.key !== 'Tab') return;
      // A minimal focus cycle: keep Tab inside the panel. A full roving
      // implementation is not needed — the panel is the boundary.
      const panel = panelRef.current;
      if (!panel) return;
      const focusables = panel.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusables.length === 0) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      // Return focus to whatever opened the modal — without this a keyboard
      // user is dropped at the top of the document.
      openerRef.current?.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  return (
    <div
      className={[
        'dpr-modal__scrim',
        // A stacked modal suppresses its own scrim and rides one layer higher: two
        // scrims compound to near-opaque, which makes the parent modal look broken
        // rather than backgrounded.
        stacked ? 'dpr-modal__scrim--stacked' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onPointerDown={(e) => {
        // Scrim click closes; a click that starts inside the panel does not.
        if (e.target === e.currentTarget) onOpenChange(false);
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={[
          'dpr-modal',
          // `narrow` is the deprecated alias; an explicit `width` always wins.
          `dpr-modal--${width ?? (narrow ? 'sm' : 'lg')}`,
        ].join(' ')}
      >
        <div className="dpr-modal__header">
          <div className="dpr-modal__header-text">
            {eyebrow ? <span className="dpr-modal__eyebrow">{eyebrow}</span> : null}
            <h2 className="dpr-modal__title">{title}</h2>
            {subtitle ? <p className="dpr-modal__subtitle">{subtitle}</p> : null}
            {progress ? <div className="dpr-modal__progress">{progress}</div> : null}
          </div>
          <IconButton
            size="sm"
            variant="ghost"
            icon={<Icon name="close" size={16} />}
            label={closeLabel}
            onClick={() => onOpenChange(false)}
          />
        </div>

        <div className="dpr-modal__body">{children}</div>

        {hint || actions ? (
          <div className="dpr-modal__footer">
            {hint ? <span className="dpr-modal__hint">{hint}</span> : null}
            {actions}
          </div>
        ) : null}
      </div>
    </div>
  );
}
